/**
 * Investors, bike ownership over time, and the remittance distribution report.
 *
 * Every write here can move who a week's money belongs to, so each one
 * invalidates the remittance weeks and Finance as well as the investors.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  BikeDetail,
  BikeOwnershipChange,
  InvestorDetail,
  InvestorEarningRow,
  InvestorBikeEndInput,
  InvestorBikeLinkInput,
  InvestorEntitlementInput,
  InvestorInput,
  InvestorLinkCorrectionInput,
  LinkCorrectionResult,
  InvestorPayoutInput,
  InvestorPayoutView,
  InvestorView,
  InvestorWeeks,
  RemittanceDistribution,
  RemittanceDistributionQuery,
  SplitChangeResult,
} from '@cenox/shared';
import { api } from '../../lib/api';
import { useBusinessPath } from '../../lib/auth';

export interface InvestorOption {
  id: string;
  name: string;
  reference: string | null;
}

function useInvalidateInvestorMoney(): () => Promise<void> {
  const client = useQueryClient();
  return async () => {
    await client.invalidateQueries({ queryKey: ['riderFinance'] });
    await client.invalidateQueries({ queryKey: ['investments'] });
    await client.invalidateQueries({ queryKey: ['riderDirectory'] });
    await client.invalidateQueries({ queryKey: ['finance'] });
    await client.invalidateQueries({ queryKey: ['accountability'] });
    await client.invalidateQueries({ queryKey: ['dashboard'] });
    await client.invalidateQueries({ queryKey: ['dashboard-growth'] });
  };
}

export function useInvestorOptions(enabled = true): UseQueryResult<InvestorOption[]> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: ['riderFinance', 'investorOptions', path('')],
    queryFn: async () => (await api.get<{ data: InvestorOption[] }>(path('/rider-finance/investors/options'))).data,
    enabled,
    staleTime: 60_000,
  });
}

export function useInvestors(): UseQueryResult<InvestorView[]> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: ['riderFinance', 'investors', path('')],
    queryFn: async () => (await api.get<{ data: InvestorView[] }>(path('/rider-finance/investors'))).data,
    staleTime: 30_000,
  });
}

export function useInvestor(id: string | null): UseQueryResult<InvestorDetail> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: ['riderFinance', 'investor', path(''), id],
    queryFn: async () => (await api.get<{ data: InvestorDetail }>(path(`/rider-finance/investors/${id!}`))).data,
    enabled: id !== null,
    staleTime: 15_000,
  });
}

/** A partnership's weekly earnings for one investor (0171). */
export function useInvestorEarnings(investorId: string | null): UseQueryResult<InvestorEarningRow[]> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: ['riderFinance', 'investorEarnings', path(''), investorId],
    queryFn: async () => (await api.get<{ data: InvestorEarningRow[] }>(path(`/rider-finance/investors/${investorId!}/earnings`))).data,
    enabled: investorId !== null,
    staleTime: 15_000,
  });
}

/** Confirm an earning under review, or reverse one. */
export function useReviewInvestorEarning(): UseMutationResult<
  unknown,
  Error,
  { id: string; action: 'confirm'; amount?: string; note: string } | { id: string; action: 'reverse'; reason: string }
> {
  const path = useBusinessPath();
  const invalidate = useInvalidateInvestorMoney();
  return useMutation({
    mutationFn: (v) =>
      v.action === 'confirm'
        ? api.post(path(`/rider-finance/investor-earnings/${v.id}/confirm`), { ...(v.amount ? { amount: v.amount } : {}), note: v.note })
        : api.post(path(`/rider-finance/investor-earnings/${v.id}/reverse`), { reason: v.reason }),
    onSuccess: invalidate,
  });
}

export function useCreateInvestor(): UseMutationResult<{ data: InvestorDetail }, Error, InvestorInput> {
  const path = useBusinessPath();
  const invalidate = useInvalidateInvestorMoney();
  return useMutation({
    // The investor register is Finance's: any business, fleet or not.
    mutationFn: (input) => api.post<{ data: InvestorDetail }>(path('/investments/investors'), input),
    onSuccess: invalidate,
  });
}

export function useUpdateInvestor(): UseMutationResult<
  { data: InvestorDetail },
  Error,
  { id: string; input: Partial<InvestorInput> }
> {
  const path = useBusinessPath();
  const invalidate = useInvalidateInvestorMoney();
  return useMutation({
    mutationFn: ({ id, input }) => api.patch<{ data: InvestorDetail }>(path(`/investments/investors/${id}`), input),
    onSuccess: invalidate,
  });
}

export function useRecordInvestorPayout(): UseMutationResult<
  { data: InvestorPayoutView; replayed: boolean },
  Error,
  { investorId: string; input: InvestorPayoutInput }
> {
  const path = useBusinessPath();
  const invalidate = useInvalidateInvestorMoney();
  return useMutation({
    mutationFn: ({ investorId, input }) =>
      api.post<{ data: InvestorPayoutView; replayed: boolean }>(
        path(`/rider-finance/investors/${investorId}/payouts`),
        input,
      ),
    onSuccess: invalidate,
  });
}

export function useVoidInvestorPayout(): UseMutationResult<
  { data: InvestorPayoutView },
  Error,
  { id: string; reason: string }
> {
  const path = useBusinessPath();
  const invalidate = useInvalidateInvestorMoney();
  return useMutation({
    mutationFn: ({ id, reason }) =>
      api.post<{ data: InvestorPayoutView }>(path(`/rider-finance/investor-payouts/${id}/void`), { reason }),
    onSuccess: invalidate,
  });
}

export function useChangeBikeOwnership(): UseMutationResult<
  { data: BikeDetail } & SplitChangeResult,
  Error,
  { bikeId: string; input: BikeOwnershipChange }
> {
  const path = useBusinessPath();
  const invalidate = useInvalidateInvestorMoney();
  return useMutation({
    mutationFn: ({ bikeId, input }) =>
      api.post<{ data: BikeDetail } & SplitChangeResult>(path(`/rider-finance/bikes/${bikeId}/ownership`), input),
    onSuccess: invalidate,
  });
}

export function useSetInvestorEntitlement(): UseMutationResult<
  { data: BikeDetail } & SplitChangeResult,
  Error,
  { bikeId: string; input: InvestorEntitlementInput }
> {
  const path = useBusinessPath();
  const invalidate = useInvalidateInvestorMoney();
  return useMutation({
    mutationFn: ({ bikeId, input }) =>
      api.post<{ data: BikeDetail } & SplitChangeResult>(
        path(`/rider-finance/bikes/${bikeId}/investor-entitlements`),
        input,
      ),
    onSuccess: invalidate,
  });
}

/** Link a bike to this investor from a date (and, optionally, their weekly share). */
export function useLinkInvestorBike(): UseMutationResult<
  { data: InvestorDetail; replayed: boolean } & SplitChangeResult,
  Error,
  { investorId: string; input: InvestorBikeLinkInput }
> {
  const path = useBusinessPath();
  const invalidate = useInvalidateInvestorMoney();
  return useMutation({
    mutationFn: ({ investorId, input }) =>
      api.post<{ data: InvestorDetail; replayed: boolean } & SplitChangeResult>(
        path(`/rider-finance/investors/${investorId}/bikes`),
        input,
      ),
    onSuccess: invalidate,
  });
}

/** End this investor's ownership of a bike: theirs through `endsOn`. */
export function useEndInvestorBike(): UseMutationResult<
  { data: InvestorDetail; replayed: boolean } & SplitChangeResult,
  Error,
  { investorId: string; bikeId: string; input: InvestorBikeEndInput }
> {
  const path = useBusinessPath();
  const invalidate = useInvalidateInvestorMoney();
  return useMutation({
    mutationFn: ({ investorId, bikeId, input }) =>
      api.post<{ data: InvestorDetail; replayed: boolean } & SplitChangeResult>(
        path(`/rider-finance/investors/${investorId}/bikes/${bikeId}/end`),
        input,
      ),
    onSuccess: invalidate,
  });
}

/** What correcting a link's dates would do. Saves nothing, so it refreshes nothing. */
export function usePreviewLinkCorrection(): UseMutationResult<
  { data: LinkCorrectionResult },
  Error,
  { investorId: string; bikeId: string; input: InvestorLinkCorrectionInput }
> {
  const path = useBusinessPath();
  return useMutation({
    mutationFn: ({ investorId, bikeId, input }) =>
      api.post<{ data: LinkCorrectionResult }>(path(`/rider-finance/investors/${investorId}/bikes/${bikeId}/correction/preview`), input),
  });
}

/** Correct a link's dates and recalculate, atomically. Refreshes every figure it can move. */
export function useSaveLinkCorrection(): UseMutationResult<
  { data: LinkCorrectionResult },
  Error,
  { investorId: string; bikeId: string; input: InvestorLinkCorrectionInput }
> {
  const path = useBusinessPath();
  const invalidate = useInvalidateInvestorMoney();
  return useMutation({
    mutationFn: ({ investorId, bikeId, input }) =>
      api.post<{ data: LinkCorrectionResult }>(path(`/rider-finance/investors/${investorId}/bikes/${bikeId}/correction`), input),
    onSuccess: invalidate,
  });
}

/** One investor's weeks in a period, under the sidebar's location scope. */
export function useInvestorWeeks(
  id: string | null,
  query: { from: string; to: string } & Record<string, string | undefined>,
  enabled = true,
): UseQueryResult<InvestorWeeks> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: ['riderFinance', 'investorWeeks', path(''), id, query],
    queryFn: () => {
      const search = new URLSearchParams();
      for (const [k, v] of Object.entries(query)) if (v) search.set(k, v);
      return api.get<InvestorWeeks>(path(`/rider-finance/investors/${id!}/weeks?${search.toString()}`));
    },
    enabled: enabled && id !== null,
    staleTime: 15_000,
  });
}

export function useRemittanceDistribution(
  query: RemittanceDistributionQuery | null,
): UseQueryResult<RemittanceDistribution> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: ['finance', 'remittanceDistribution', path(''), query],
    queryFn: () => {
      const search = new URLSearchParams();
      for (const [k, v] of Object.entries(query!)) if (v) search.set(k, String(v));
      return api.get<RemittanceDistribution>(path(`/rider-finance/remittance/distribution?${search.toString()}`));
    },
    enabled: query !== null,
    staleTime: 15_000,
  });
}

/** "Collected for the investor: 3 unpaid weeks now use the new figure; 1 with payments kept its own." */
export function describeSplitChange(result: SplitChangeResult): string {
  const parts: string[] = [];
  parts.push(
    result.restampedWeeks === 0
      ? 'No charged week needed changing.'
      : `${result.restampedWeeks} unpaid ${result.restampedWeeks === 1 ? 'week now uses' : 'weeks now use'} the new terms.`,
  );
  if (result.keptWeeks > 0) {
    parts.push(
      `${result.keptWeeks} ${result.keptWeeks === 1 ? 'week' : 'weeks'} with payments already recorded kept the split ${result.keptWeeks === 1 ? 'it was' : 'they were'} paid under.`,
    );
  }
  return parts.join(' ');
}
